/* =====================================================================
 * Google Analytics 4 — opt-in runtime gate
 *
 * Gate order (Phase 8):
 *   1. Tenant manifest — `analytics.ga4.enabled !== true` short-
 *      circuits the entire pipeline and no gtag.js is loaded.
 *   2. Env var `VITE_GA_MEASUREMENT_ID` — when the tenant does not
 *      override the id, the env var supplies the legacy B2C value.
 *   3. Cookie consent — user must opt into analytics storage.
 *
 * Design
 *   - 광고 관련 consent는 항상 denied 유지
 *   - SPA 페이지뷰는 수동 관리 (send_page_view: false)
 *   - 테넌트 gate 가 false 면 스크립트는 절대 appendChild 되지 않음.
 * ===================================================================== */

import { getTenantSnapshot } from '@auw/onprem';
import { resolveTenantAnalyticsGa4 } from '@auw/tenant';

let ga4Initialized = false;
let ga4Active = false;
let ga4StatusListeners: Array<() => void> = [];

/**
 * Tenant-aware GA4 measurement id. Returns an empty string when the
 * tenant has GA4 disabled OR neither the tenant manifest nor the env
 * var supplies a concrete id — either way the caller treats this as
 * "GA4 unavailable".
 */
function getMeasurementId(): string {
  const envId = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) ?? '';
  const tenant = getTenantSnapshot();
  if (!tenant) return envId;
  const ga4 = resolveTenantAnalyticsGa4(tenant);
  if (!ga4) return '';
  return (typeof ga4.measurementId === 'string' && ga4.measurementId.trim()) || envId;
}

function isDebugMode(): boolean {
  return (import.meta.env.VITE_GA_DEBUG as string | undefined) === 'true';
}

function ensureDataLayer(): void {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    // gtag.js queues the raw arguments object, not a copied array.
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
}

function emitGA4Status(): void {
  for (const listener of ga4StatusListeners) {
    listener();
  }
}

function setGA4Active(nextActive: boolean): void {
  if (ga4Active === nextActive) return;
  ga4Active = nextActive;
  emitGA4Status();
}

function getConsentParams(analyticsEnabled: boolean): GtagConsentParams {
  return {
    analytics_storage: analyticsEnabled ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  } satisfies GtagConsentParams;
}

function ensureGA4Script(measurementId: string): void {
  if (typeof document === 'undefined') return;
  if (document.querySelector('script[data-auw-ga4="true"]')) return;

  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.async = true;
  script.dataset.auwGa4 = 'true';
  document.head.appendChild(script);
}

/* ── 초기화 ── */

export function initTracking(): void {
  ensureDataLayer();
  ensureGA4Initialized();
}

export function ensureGA4Initialized(): boolean {
  if (ga4Initialized) return true;

  const measurementId = getMeasurementId();
  if (!measurementId) return false;

  ensureDataLayer();

  window.gtag('consent', 'default', getConsentParams(false));

  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false,
    debug_mode: isDebugMode(),
  });

  ensureGA4Script(measurementId);

  ga4Initialized = true;
  return true;
}

/* ── Consent 업데이트 ── */

export function updateGA4Consent(categories: {
  analytics?: boolean;
  marketing?: boolean;
}): void {
  const analyticsEnabled = categories.analytics === true;
  if (!analyticsEnabled && !ga4Initialized) {
    setGA4Active(false);
    return;
  }

  if (!ensureGA4Initialized()) {
    setGA4Active(false);
    return;
  }

  window.gtag('consent', 'update', getConsentParams(analyticsEnabled));
  setGA4Active(analyticsEnabled);
}

export function subscribeGA4Active(listener: () => void): () => void {
  ga4StatusListeners.push(listener);
  return () => {
    ga4StatusListeners = ga4StatusListeners.filter((entry) => entry !== listener);
  };
}

/* ── 이벤트 전송 ── */

export function trackEvent(
  name: string,
  params?: Record<string, unknown>,
): void {
  if (!ga4Active) return;
  window.gtag('event', name, params);
}

export function trackPageView(path: string, title?: string): void {
  if (!ga4Active) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title ?? document.title,
    page_location: window.location.href,
  });
}

/* ── 사용자 속성 ── */

export function setGA4UserProperties(
  properties: Record<string, string | number | boolean>,
): void {
  if (!ga4Active) return;
  window.gtag('set', 'user_properties', properties);
}

export function isGA4Active(): boolean {
  return ga4Active;
}
